import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Trash2, Save, Upload, X as XIcon, Loader2, Package, Boxes, CheckCircle2, Clock, Truck, Copy, MessageSquare, KeyRound, Mail, Globe, Ticket } from "lucide-react";
import { useAuth } from "../lib/store";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { SectionHead } from "./Home";
import { VouchersPanel } from "../components/VouchersPanel";
import { DocumentsPanel } from "../components/DocumentsPanel";
import { FileText } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

const ImageUploader = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const preview = resolveImg(value);

  return (
    <div className="space-y-2">
      {preview ? (
        <div className="relative border border-[#C9A227]/30 rounded-sm bg-[#0b0b0d] p-2">
          <img src={preview} alt="preview" className="max-h-48 w-full object-contain rounded-sm" />
          <button
            type="button"
            data-testid="image-remove"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center bg-black/80 border border-[#C9A227]/40 hover:border-[#DC2626] hover:text-[#DC2626] text-white rounded-sm transition-colors"
          >
            <XIcon size={14} />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => !busy && inputRef.current?.click()}
          data-testid="image-drop-area"
          className={`border-2 border-dashed rounded-sm p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
            drag ? "border-[#C9A227] bg-[#C9A227]/8" : "border-[#C9A227]/25 hover:border-[#C9A227]/50"
          } ${busy ? "opacity-60 pointer-events-none" : ""}`}
        >
          {busy ? (
            <Loader2 size={20} className="text-[#C9A227] animate-spin" />
          ) : (
            <Upload size={20} className="text-[#C9A227]" />
          )}
          <div className="text-sm text-white font-semibold">
            {busy ? "Caricamento…" : "Clicca o trascina un'immagine"}
          </div>
          <div className="text-[10px] font-mono-ps text-[#9CA3AF] tracking-widest uppercase">
            JPG • PNG • WEBP • Max 8 MB
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid="image-file-input"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
          e.target.value = "";
        }}
      />
      {value && (
        <div className="text-[10px] font-mono-ps text-[#9CA3AF] break-all">
          Path: <span className="text-[#C9A227]">{value}</span>
        </div>
      )}
    </div>
  );
};

const EMPTY = {
  name: "", slug: "", category: "op-preorder-jp", game: "one-piece", product_type: "booster-box",
  language: "jp", price: 0, old_price: null, stock: 0, is_preorder: false, release_date: "",
  preorder_type: "", deposit_percent: null, delivery_time: "15-20 giorni lavorativi",
  condition: "sealed", set_code: "", product_code: "", description: "", image_url: "",
  badges: [], featured: false,
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({ count: 0, with_balance: 0, revenue_paid: 0, balance_pending: 0 });
  const [orderFilter, setOrderFilter] = useState({ status: "", has_balance: "" });
  const [requests, setRequests] = useState([]);

  const load = () => api.get("/products", { params: { limit: 200 } }).then(({ data }) => setProducts(data));
  const loadOrders = () => {
    const params = {};
    if (orderFilter.status) params.status = orderFilter.status;
    if (orderFilter.has_balance === "yes") params.has_balance = true;
    if (orderFilter.has_balance === "no") params.has_balance = false;
    return api.get("/admin/orders", { params }).then(({ data }) => {
      setOrders(data.orders || []);
      setOrderStats(data.stats || {});
    });
  };
  const loadRequests = () =>
    api.get("/admin/product-requests").then(({ data }) => setRequests(data));

  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "orders") loadOrders(); }, [tab, orderFilter]);
  useEffect(() => { if (tab === "requests") loadRequests(); }, [tab]);

  if (loading) return <div className="p-10 text-[#9CA3AF]">…</div>;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing,
        price: parseFloat(editing.price) || 0,
        old_price: editing.old_price ? parseFloat(editing.old_price) : null,
        stock: parseInt(editing.stock) || 0,
        deposit_percent: editing.deposit_percent ? parseFloat(editing.deposit_percent) : null,
        preorder_type: editing.preorder_type || null,
        release_date: editing.release_date || null,
        delivery_time: editing.delivery_time || null,
        image_url: editing.image_url || null,
        badges: typeof editing.badges === "string" ? editing.badges.split(",").map((s) => s.trim()).filter(Boolean) : editing.badges,
      };
      if (editing.product_id) {
        await api.put(`/admin/products/${editing.product_id}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Prodotto creato");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 md:py-14">
      <SectionHead
        eyebrow="Admin"
        title="Backoffice PULL STATION"
        subtitle={tab === "products" ? `${products.length} prodotti in catalogo` : tab === "orders" ? `${orderStats.count} ordini in totale` : null}
      />

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-[#C9A227]/15 pb-3">
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} icon={<Boxes size={14} />} label="Prodotti" testid="admin-tab-products" />
        <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={<Package size={14} />} label="Ordini" testid="admin-tab-orders" />
        <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} icon={<MessageSquare size={14} />} label="Richieste" testid="admin-tab-requests" />
        <TabBtn active={tab === "vouchers"} onClick={() => setTab("vouchers")} icon={<Ticket size={14} />} label="Buoni" testid="admin-tab-vouchers" />
        <TabBtn active={tab === "documents"} onClick={() => setTab("documents")} icon={<FileText size={14} />} label="Ricevute" testid="admin-tab-documents" />
        <TabBtn active={tab === "password"} onClick={() => setTab("password")} icon={<KeyRound size={14} />} label="Password" testid="admin-tab-password" />
        <TabBtn active={tab === "site"} onClick={() => setTab("site")} icon={<Globe size={14} />} label="Sito" testid="admin-tab-site" />
      </div>

      {tab === "products" && (
        <>
          <div className="mt-6 flex justify-end">
            <button data-testid="admin-new" onClick={() => setEditing({ ...EMPTY })} className="btn-gold">
              <Plus size={16} /> Nuovo prodotto
            </button>
          </div>

          <div className="mt-6 border border-[#C9A227]/15 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0b0b0d] border-b border-[#C9A227]/15">
                  <tr className="text-left">
                    {["Nome", "Categoria", "Lang", "Prezzo", "Stock", "Preorder", "Azioni"].map((h) => (
                      <th key={h} className="px-3 py-3 font-mono-ps text-[10px] tracking-widest uppercase text-[#C9A227]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.product_id} className="border-b border-[#C9A227]/8 hover:bg-[#C9A227]/5">
                      <td className="px-3 py-3 text-white">{p.name}</td>
                      <td className="px-3 py-3 text-[#9CA3AF]">{p.category}</td>
                      <td className="px-3 py-3 font-mono-ps text-[#9CA3AF]">{p.language?.toUpperCase()}</td>
                      <td className="px-3 py-3 font-mono-ps text-[#C9A227]">€ {p.price.toFixed(2)}</td>
                      <td className="px-3 py-3 font-mono-ps text-white">{p.stock}</td>
                      <td className="px-3 py-3">{p.is_preorder ? <span className="ps-badge text-[#D97706] border-[#D97706]">YES</span> : "—"}</td>
                      <td className="px-3 py-3 flex gap-2">
                        <button data-testid={`edit-${p.slug}`} onClick={() => setEditing({ ...p })} className="text-[#C9A227] hover:text-[#F3D068] text-xs uppercase font-mono-ps tracking-widest">Edit</button>
                        <button data-testid={`del-${p.slug}`} onClick={() => del(p.product_id)} className="text-[#DC2626] hover:text-red-400"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "orders" && (
        <OrdersPanel
          orders={orders}
          stats={orderStats}
          filter={orderFilter}
          setFilter={setOrderFilter}
          refresh={loadOrders}
        />
      )}

      {tab === "requests" && (
        <RequestsPanel requests={requests} refresh={loadRequests} />
      )}

      {tab === "vouchers" && <VouchersPanel />}
      {tab === "documents" && <DocumentsPanel />}

      {tab === "site" && <SitePanel />}

      {tab === "password" && (
        <PasswordPanel />
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-3xl bg-[#0b0b0d] border border-[#C9A227]/30 rounded-sm p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-xl font-bold text-white mb-4">
              {editing.product_id ? "Modifica prodotto" : "Nuovo prodotto"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Nome"><I data-testid="f-name" v={editing.name} on={(v) => setEditing({ ...editing, name: v })} /></F>
              <F label="Slug"><I data-testid="f-slug" v={editing.slug} on={(v) => setEditing({ ...editing, slug: v })} /></F>
              <F label="Categoria">
                <Sel v={editing.category} on={(v) => setEditing({ ...editing, category: v })}
                  options={["op-preorder-jp", "op-preorder-en", "singles", "pkm-sealed-it", "pkm-sealed-jp", "accessori"]} />
              </F>
              <F label="Game">
                <Sel v={editing.game} on={(v) => setEditing({ ...editing, game: v })} options={["one-piece", "pokemon", "other"]} />
              </F>
              <F label="Tipo"><I v={editing.product_type} on={(v) => setEditing({ ...editing, product_type: v })} /></F>
              <F label="Lingua"><Sel v={editing.language} on={(v) => setEditing({ ...editing, language: v })} options={["jp", "en", "it", "other"]} /></F>
              <F label="Prezzo €"><I type="number" step="0.01" v={editing.price} on={(v) => setEditing({ ...editing, price: v })} /></F>
              <F label="Prezzo vecchio €"><I type="number" step="0.01" v={editing.old_price || ""} on={(v) => setEditing({ ...editing, old_price: v })} /></F>
              <F label="Stock"><I type="number" v={editing.stock} on={(v) => setEditing({ ...editing, stock: v })} /></F>
              <F label="Condizione"><Sel v={editing.condition} on={(v) => setEditing({ ...editing, condition: v })} options={["sealed", "near-mint", "graded"]} /></F>
              <F label="Set code"><I v={editing.set_code || ""} on={(v) => setEditing({ ...editing, set_code: v })} /></F>
              <F label="Codice"><I v={editing.product_code || ""} on={(v) => setEditing({ ...editing, product_code: v })} /></F>
              <F label="Badges (comma)"><I v={Array.isArray(editing.badges) ? editing.badges.join(", ") : editing.badges} on={(v) => setEditing({ ...editing, badges: v })} /></F>
              <F label="Preorder">
                <label className="flex items-center gap-2 text-white pt-2"><input type="checkbox" checked={!!editing.is_preorder} onChange={(e) => setEditing({ ...editing, is_preorder: e.target.checked })} className="accent-[#C9A227]" /> Sì</label>
              </F>
              <F label="Tipo preorder">
                <Sel v={editing.preorder_type || ""} on={(v) => setEditing({ ...editing, preorder_type: v })}
                  options={["", "full", "allocation-deposit"]} />
              </F>
              <F label="Acconto % (allocation)"><I type="number" step="1" v={editing.deposit_percent ?? ""} on={(v) => setEditing({ ...editing, deposit_percent: v })} /></F>
              <F label="Data uscita"><I type="date" v={editing.release_date || ""} on={(v) => setEditing({ ...editing, release_date: v })} /></F>
              <F label="Tempi consegna"><I v={editing.delivery_time || ""} on={(v) => setEditing({ ...editing, delivery_time: v })} placeholder="es. 15-20 giorni lavorativi" /></F>
              <F label="Featured">
                <label className="flex items-center gap-2 text-white pt-2"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#C9A227]" /> Sì</label>
              </F>
              <div className="sm:col-span-2">
                <F label="Immagine prodotto">
                  <ImageUploader
                    value={editing.image_url || ""}
                    onChange={(v) => setEditing({ ...editing, image_url: v })}
                  />
                </F>
              </div>
              <div className="sm:col-span-2">
                <F label="Descrizione">
                  <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none resize-none" />
                </F>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost-gold">Annulla</button>
              <button data-testid="admin-save" onClick={save} disabled={busy} className="btn-gold disabled:opacity-60"><Save size={16} /> Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</span>
    {children}
  </label>
);
const I = ({ v, on, ...rest }) => (
  <input {...rest} value={v} onChange={(e) => on(e.target.value)}
    className="bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 text-white outline-none text-base" />
);
const Sel = ({ v, on, options }) => (
  <select value={v} onChange={(e) => on(e.target.value)}
    className="bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 text-white outline-none">
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

/* ---------- Tab button ---------- */
const TabBtn = ({ active, onClick, icon, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-xs font-mono-ps uppercase tracking-widest border rounded-sm transition-colors ${
      active
        ? "border-[#C9A227] text-[#C9A227] bg-[#C9A227]/5"
        : "border-[#C9A227]/15 text-white/70 hover:text-[#C9A227]"
    }`}
  >
    {icon}{label}
  </button>
);

/* ---------- Orders Panel ---------- */
const STATUS_META = {
  "awaiting-transfer": { label: "In attesa bonifico", color: "text-[#D97706] border-[#D97706]" },
  "confirmed":         { label: "Confermato",       color: "text-green-500 border-green-500" },
  "deposit-paid":      { label: "Acconto ricevuto", color: "text-[#C9A227] border-[#C9A227]" },
  "balance-requested": { label: "Saldo richiesto",  color: "text-[#D97706] border-[#D97706]" },
  "balance-paid":      { label: "Saldo pagato",     color: "text-green-500 border-green-500" },
  "shipped":           { label: "Spedito",          color: "text-[#60A5FA] border-[#60A5FA]" },
  "delivered":         { label: "Consegnato",       color: "text-green-500 border-green-500" },
  "cancelled":         { label: "Annullato",        color: "text-[#DC2626] border-[#DC2626]" },
  "returned-voucher":  { label: "Reso — buono emesso", color: "text-emerald-400 border-emerald-400" },
};

const OrdersPanel = ({ orders, stats, filter, setFilter, refresh }) => {
  const [selected, setSelected] = useState(null);

  return (
    <div className="mt-6 space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={<Package size={16} />} label="Ordini totali" value={stats.count || 0} />
        <Stat icon={<CheckCircle2 size={16} />} label="Incasso oggi" value={`€ ${(stats.revenue_paid || 0).toFixed(2)}`} highlight />
        <Stat icon={<Clock size={16} />} label="Con saldo aperto" value={stats.with_balance || 0} />
        <Stat icon={<Truck size={16} />} label="Saldo da riscuotere" value={`€ ${(stats.balance_pending || 0).toFixed(2)}`} highlight />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 border border-[#C9A227]/15 rounded-sm p-3 bg-[#0b0b0d]">
        <label className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">Status</label>
        <select
          data-testid="orders-filter-status"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="bg-black border border-[#C9A227]/25 rounded-sm px-2 py-1.5 text-sm text-white outline-none"
        >
          <option value="">Tutti</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <label className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF] ml-3">Saldo</label>
        <select
          data-testid="orders-filter-balance"
          value={filter.has_balance}
          onChange={(e) => setFilter({ ...filter, has_balance: e.target.value })}
          className="bg-black border border-[#C9A227]/25 rounded-sm px-2 py-1.5 text-sm text-white outline-none"
        >
          <option value="">Tutti</option>
          <option value="yes">Con saldo aperto</option>
          <option value="no">Saldo chiuso</option>
        </select>
      </div>

      {/* Orders table */}
      <div className="border border-[#C9A227]/15 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0b0b0d] border-b border-[#C9A227]/15">
              <tr className="text-left">
                {["Ordine", "Data", "Cliente", "Articoli", "Incasso", "Saldo aperto", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-3 font-mono-ps text-[10px] tracking-widest uppercase text-[#C9A227]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-[#9CA3AF]">Nessun ordine con questi filtri.</td></tr>
              )}
              {orders.map((o) => {
                const meta = STATUS_META[o.status] || { label: o.status, color: "text-white border-white/30" };
                return (
                  <tr key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="border-b border-[#C9A227]/8 hover:bg-[#C9A227]/5">
                    <td className="px-3 py-3 font-mono-ps text-[#C9A227]">#{o.order_id.slice(-8).toUpperCase()}</td>
                    <td className="px-3 py-3 text-[#9CA3AF] text-xs">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="px-3 py-3 text-white">
                      <div className="text-white">{o.shipping?.name || "—"}</div>
                      <div className="text-[10px] text-[#9CA3AF]">{o.user_email}</div>
                    </td>
                    <td className="px-3 py-3 text-[#9CA3AF]">{o.items?.length || 0}</td>
                    <td className="px-3 py-3 font-mono-ps text-white">€ {o.total?.toFixed(2)}</td>
                    <td className="px-3 py-3 font-mono-ps">
                      {o.balance_due > 0 ? (
                        <span className="text-[#C9A227]">€ {o.balance_due.toFixed(2)}</span>
                      ) : <span className="text-[#4B5563]">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`ps-badge ${meta.color}`}>{meta.label}</span>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        data-testid={`admin-open-${o.order_id}`}
                        onClick={() => setSelected(o)}
                        className="text-[#C9A227] hover:text-[#F3D068] text-xs uppercase font-mono-ps tracking-widest"
                      >
                        Apri
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderDetail order={selected} onClose={() => setSelected(null)} refresh={refresh} />
      )}
    </div>
  );
};

const Stat = ({ icon, label, value, highlight }) => (
  <div className="border border-[#C9A227]/15 rounded-sm p-4 bg-[#0b0b0d]">
    <div className="flex items-center gap-2 text-[#C9A227]">{icon}
      <span className="text-[10px] font-mono-ps uppercase tracking-widest">{label}</span>
    </div>
    <div className={`font-display font-bold text-2xl mt-2 ${highlight ? "text-[#C9A227]" : "text-white"}`}>{value}</div>
  </div>
);

const OrderDetail = ({ order, onClose, refresh }) => {
  const [status, setStatus] = useState(order.status);
  const [tracking, setTracking] = useState(order.tracking_code || "");
  const [note, setNote] = useState(order.admin_note || "");
  const [saving, setSaving] = useState(false);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.order_id}`, { status, tracking_code: tracking, admin_note: note, ...extra });
      toast.success("Ordine aggiornato");
      refresh();
      onClose();
    } catch (e) { toast.error("Errore aggiornamento"); }
    finally { setSaving(false); }
  };

  const markBalancePaid = () => save({ balance_paid: true, status: "balance-paid" });
  const requestBalance = () => save({ status: "balance-requested" });
  const markShipped = () => save({ status: "shipped" });
  const [confirmVoucher, setConfirmVoucher] = useState(false);
  const voucherAllowed = order.user_id && !["awaiting-transfer", "cancelled"].includes(order.status);
  const issueReturnVoucher = async () => {
    setConfirmVoucher(false);
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/return-voucher`);
      navigator.clipboard?.writeText(data.code);
      toast.success(`Buono ${data.code} da € ${data.initial_amount.toFixed(2)} creato e copiato`);
      refresh();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione buono"); }
    finally { setSaving(false); }
  };

  const hasBalance = order.balance_due > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#0b0b0d] border border-[#C9A227]/30 rounded-sm max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#C9A227]/15 flex justify-between items-start">
          <div>
            <div className="eyebrow">Ordine #{order.order_id.slice(-8).toUpperCase()}</div>
            <h3 className="font-display text-xl font-bold text-white mt-1">{order.shipping?.name}</h3>
            <div className="text-xs text-[#9CA3AF]">{order.user_email} • {new Date(order.created_at).toLocaleString("it-IT")}</div>
          </div>
          <button onClick={onClose} data-testid="admin-order-close" className="p-2 text-white hover:text-[#C9A227]"><XIcon size={18} /></button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-5">
          <div className="border border-[#C9A227]/15 rounded-sm p-4 bg-[#121214]">
            <div className="eyebrow mb-2">Spedizione</div>
            <div className="text-sm text-white">{order.shipping?.address}</div>
            <div className="text-sm text-[#9CA3AF]">{order.shipping?.zip} {order.shipping?.city} — {order.shipping?.country}</div>
            {order.shipping_method && (
              <div className="text-xs text-[#C9A227] font-mono-ps uppercase tracking-widest mt-2">
                Corriere: {order.shipping_method === "vinted" ? "Vinted" : order.shipping_method === "brt" ? "BRT 24/48h" : order.shipping_method === "brt-insured" ? "BRT 24/48h Assicurata" : order.shipping_method}
              </div>
            )}
            <div className="text-xs text-[#9CA3AF] mt-2">Metodo pagamento: {order.payment_method}</div>
          </div>
          <div className="border border-[#C9A227]/15 rounded-sm p-4 bg-[#121214]">
            <div className="eyebrow mb-2">Riepilogo</div>
            <Row k="Incassato oggi" v={`€ ${order.total?.toFixed(2)}`} strong />
            {hasBalance && <Row k="Saldo aperto" v={`€ ${order.balance_due.toFixed(2)}`} gold />}
            {order.shipping_cost !== undefined && <Row k="Spedizione" v={`€ ${order.shipping_cost.toFixed(2)}`} />}
            {order.voucher_discount > 0 && <Row k={`Buono ${order.voucher_code}`} v={`− € ${order.voucher_discount.toFixed(2)}`} />}
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="eyebrow mb-2">Articoli</div>
          <div className="border border-[#C9A227]/15 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#121214]">
                <tr className="text-left">
                  {["Prodotto", "Qtà", "P. unit.", "Acconto", "Saldo", "Oggi"].map((h) => (
                    <th key={h} className="px-3 py-2 font-mono-ps text-[10px] tracking-widest uppercase text-[#C9A227]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {order.items?.map((it) => (
                  <tr key={it.product_id} className="border-t border-[#C9A227]/8">
                    <td className="px-3 py-2 text-white">
                      {it.name}
                      {it.preorder_type === "allocation-deposit" && (
                        <span className="ml-2 ps-badge text-[#C9A227] border-[#C9A227]">ALLOC {it.deposit_percent}%</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono-ps text-[#9CA3AF]">{it.qty}</td>
                    <td className="px-3 py-2 font-mono-ps text-[#9CA3AF]">€ {it.unit_price?.toFixed(2) ?? "—"}</td>
                    <td className="px-3 py-2 font-mono-ps text-[#9CA3AF]">{it.deposit_percent ? `${it.deposit_percent}%` : "—"}</td>
                    <td className="px-3 py-2 font-mono-ps text-[#C9A227]">{it.line_balance ? `€ ${it.line_balance.toFixed(2)}` : "—"}</td>
                    <td className="px-3 py-2 font-mono-ps text-white">€ {(it.line_today ?? it.line_total ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-6 border-t border-[#C9A227]/15 grid md:grid-cols-2 gap-4">
          <F label="Status">
            <fieldset disabled={!!order.return_voucher_code} className="disabled:opacity-50">
              <Sel v={status} on={setStatus} options={Object.keys(STATUS_META)} />
            </fieldset>
          </F>
          <F label="Codice tracking">
            <I v={tracking} on={setTracking} placeholder="es. LX1234567IT" />
          </F>
          <div className="md:col-span-2">
            <F label="Note admin">
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 text-white outline-none resize-none"
              />
            </F>
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-wrap gap-2 justify-end">
          {hasBalance && (
            <>
              <button
                data-testid="admin-order-request-balance"
                onClick={requestBalance}
                disabled={saving}
                className="btn-ghost-gold disabled:opacity-50"
              >
                <Clock size={14} /> Richiedi saldo
              </button>
              <button
                data-testid="admin-order-mark-paid"
                onClick={markBalancePaid}
                disabled={saving}
                className="btn-gold disabled:opacity-50"
              >
                <CheckCircle2 size={14} /> Segna saldo pagato
              </button>
            </>
          )}
          {order.return_voucher_code ? (
            <span data-testid="admin-order-return-voucher-code" className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono-ps uppercase tracking-widest border border-emerald-400/50 text-emerald-400 rounded-sm">
              <Ticket size={14} /> Buono emesso: {order.return_voucher_code}
            </span>
          ) : (
            <button
              data-testid="admin-order-return-voucher"
              onClick={() => setConfirmVoucher(true)}
              disabled={saving || !voucherAllowed}
              title={voucherAllowed ? "" : "Disponibile solo per ordini pagati di clienti registrati"}
              className="btn-ghost-gold disabled:opacity-50"
            >
              <Ticket size={14} /> Buono da reso
            </button>
          )}
          <AlertDialog open={confirmVoucher} onOpenChange={setConfirmVoucher}>
            <AlertDialogContent className="bg-[#0b0b0d] border-[#C9A227]/30 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Emettere buono da reso?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#9CA3AF]">
                  Verrà creato un buono negozio di pari valore (spedizione esclusa) intestato a {order.user_email}. L'ordine sarà segnato come "Reso — buono emesso" e non potrà più cambiare stato.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="return-voucher-cancel" className="bg-transparent border-[#C9A227]/30 text-white hover:bg-white/5 hover:text-white">Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="return-voucher-confirm" onClick={issueReturnVoucher} className="bg-[#C9A227] text-black hover:bg-[#d8b23a]">Emetti buono</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button
            data-testid="admin-order-mark-shipped"
            onClick={markShipped}
            disabled={saving || !!order.return_voucher_code}
            className="btn-ghost-gold disabled:opacity-50"
          >
            <Truck size={14} /> Segna spedito
          </button>
          <button
            data-testid="admin-order-save"
            onClick={() => save()}
            disabled={saving}
            className="btn-gold disabled:opacity-50"
          >
            <Save size={14} /> Salva
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v, strong, gold }) => (
  <div className="flex justify-between py-1 text-sm">
    <span className="text-[#9CA3AF]">{k}</span>
    <span className={`font-mono-ps ${gold ? "text-[#C9A227]" : strong ? "text-white font-bold" : "text-white"}`}>{v}</span>
  </div>
);

/* ---------- Requests Panel ---------- */
const REQ_STATUS = {
  "pending":     { label: "In attesa",     color: "text-[#D97706] border-[#D97706]" },
  "in-progress": { label: "In lavorazione", color: "text-[#60A5FA] border-[#60A5FA]" },
  "quoted":      { label: "Preventivo inviato", color: "text-[#C9A227] border-[#C9A227]" },
  "fulfilled":   { label: "Completata",     color: "text-green-500 border-green-500" },
  "rejected":    { label: "Rifiutata",      color: "text-[#DC2626] border-[#DC2626]" },
};

const RequestsPanel = ({ requests, refresh }) => {
  const [selected, setSelected] = useState(null);

  return (
    <div className="mt-6 space-y-4">
      <div className="border border-[#C9A227]/15 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0b0b0d] border-b border-[#C9A227]/15">
              <tr className="text-left">
                {["Data", "Prodotto", "Lingua", "Qtà", "Budget", "WhatsApp", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-3 font-mono-ps text-[10px] tracking-widest uppercase text-[#C9A227]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-[#9CA3AF]">Nessuna richiesta ricevuta.</td></tr>
              )}
              {requests.map((r) => {
                const meta = REQ_STATUS[r.status] || { label: r.status, color: "text-white border-white/30" };
                return (
                  <tr key={r.request_id} data-testid={`admin-request-${r.request_id}`} className="border-b border-[#C9A227]/8 hover:bg-[#C9A227]/5">
                    <td className="px-3 py-3 text-[#9CA3AF] text-xs">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="px-3 py-3 text-white">{r.product_name}</td>
                    <td className="px-3 py-3 font-mono-ps text-[#9CA3AF] uppercase">{r.language}</td>
                    <td className="px-3 py-3 font-mono-ps text-white">{r.quantity}</td>
                    <td className="px-3 py-3 font-mono-ps text-[#9CA3AF]">{r.budget ? `€ ${r.budget.toFixed(2)}` : "—"}</td>
                    <td className="px-3 py-3 text-white text-xs font-mono-ps">{r.contact_phone || r.contact_email || "—"}</td>
                    <td className="px-3 py-3"><span className={`ps-badge ${meta.color}`}>{meta.label}</span></td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => setSelected(r)}
                        data-testid={`admin-req-open-${r.request_id}`}
                        className="text-[#C9A227] hover:text-[#F3D068] text-xs uppercase font-mono-ps tracking-widest"
                      >
                        Apri
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {selected && <RequestDetail req={selected} onClose={() => setSelected(null)} refresh={refresh} />}
    </div>
  );
};

const RequestDetail = ({ req, onClose, refresh }) => {
  const [status, setStatus] = useState(req.status);
  const [note, setNote] = useState(req.admin_note || "");
  const [price, setPrice] = useState(req.quoted_price ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/product-requests/${req.request_id}`, {
        status,
        admin_note: note,
        quoted_price: price ? parseFloat(price) : null,
      });
      toast.success("Richiesta aggiornata");
      refresh();
      onClose();
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const rawPhone = (req.contact_phone || "").replace(/[^\d+]/g, "");
  const waPhone = rawPhone.startsWith("+") ? rawPhone.slice(1) : (rawPhone.startsWith("00") ? rawPhone.slice(2) : (rawPhone.startsWith("3") ? `39${rawPhone}` : rawPhone));
  const waText = encodeURIComponent(
    `Ciao! Sono PULL STATION. Abbiamo trovato il prodotto che avevi richiesto: ${req.product_name} (${req.language.toUpperCase()}) x${req.quantity}.\n\nEcco il nostro preventivo:`
  );
  const waHref = waPhone ? `https://wa.me/${waPhone}?text=${waText}` : "#";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0b0b0d] border border-[#C9A227]/30 rounded-sm max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#C9A227]/15 flex justify-between items-start">
          <div>
            <div className="eyebrow">Richiesta prodotto</div>
            <h3 className="font-display text-xl font-bold text-white mt-1">{req.product_name}</h3>
            <div className="text-xs text-[#9CA3AF] mt-1">{new Date(req.created_at).toLocaleString("it-IT")}</div>
          </div>
          <button onClick={onClose} className="p-2 text-white hover:text-[#C9A227]"><XIcon size={18} /></button>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-3 text-sm">
          <InfoLine label="Cellulare cliente (WhatsApp)" value={req.contact_phone || req.contact_email || "—"} />
          <InfoLine label="Lingua" value={req.language.toUpperCase()} />
          <InfoLine label="Quantità" value={req.quantity} />
          <InfoLine label="Budget" value={req.budget ? `€ ${req.budget.toFixed(2)}` : "—"} />
          {req.notes && (
            <div className="sm:col-span-2 border border-[#C9A227]/15 rounded-sm p-3 bg-black">
              <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF] mb-1">Note cliente</div>
              <div className="text-sm text-white whitespace-pre-wrap">{req.notes}</div>
            </div>
          )}
        </div>

        <div className="px-6 pb-4 grid sm:grid-cols-2 gap-3">
          <F label="Status">
            <Sel v={status} on={setStatus} options={Object.keys(REQ_STATUS)} />
          </F>
          <F label="Preventivo € (opzionale)">
            <I type="number" step="0.01" v={price} on={setPrice} placeholder="es. 89.90" />
          </F>
          <div className="sm:col-span-2">
            <F label="Note interne admin">
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 text-white outline-none resize-none" />
            </F>
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-wrap gap-2 justify-end">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-req-whatsapp"
            className={`btn-ghost-gold ${!waPhone ? "opacity-40 pointer-events-none" : ""}`}
          >
            <MessageSquare size={14} /> Invia preventivo su WhatsApp
          </a>
          <button onClick={save} disabled={saving} data-testid="admin-req-save" className="btn-gold disabled:opacity-60">
            <Save size={14} /> Salva
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoLine = ({ label, value }) => (
  <div className="border border-[#C9A227]/15 rounded-sm p-3 bg-black">
    <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</div>
    <div className="text-sm text-white mt-1 break-all">{value}</div>
  </div>
);

/* ---------- Password Panel ---------- */
const PasswordPanel = () => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    if (form.next.length < 8) {
      toast.error("Minimo 8 caratteri");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password aggiornata");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento password");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 max-w-md">
      <div className="border border-[#C9A227]/20 rounded-sm p-6 bg-[#0b0b0d]">
        <div className="flex items-center gap-2 text-[#C9A227]">
          <KeyRound size={16} />
          <span className="text-xs font-mono-ps uppercase tracking-widest">Cambio password</span>
        </div>
        <p className="text-sm text-[#9CA3AF] mt-2">
          Ti consigliamo di cambiare la password admin di default appena possibile.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <F label="Password attuale">
            <I type="password" v={form.current} on={(v) => setForm({ ...form, current: v })} data-testid="pw-current" />
          </F>
          <F label="Nuova password (min. 8 caratteri)">
            <I type="password" v={form.next} on={(v) => setForm({ ...form, next: v })} data-testid="pw-new" />
          </F>
          <F label="Conferma nuova password">
            <I type="password" v={form.confirm} on={(v) => setForm({ ...form, confirm: v })} data-testid="pw-confirm" />
          </F>
          <button type="submit" disabled={busy} data-testid="pw-submit" className="btn-gold w-full justify-center disabled:opacity-60">
            <Save size={14} /> {busy ? "Aggiorno…" : "Aggiorna password"}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ---------- Site Panel ---------- */
const SitePanel = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/settings/public").then(({ data }) => setOn(data.coming_soon)); }, []);

  const toggle = async () => {
    try {
      const { data } = await api.put("/admin/settings", { coming_soon: !on });
      setOn(data.coming_soon);
      window.dispatchEvent(new CustomEvent("ps-coming-soon", { detail: data.coming_soon }));
      toast.success(data.coming_soon ? "Coming Soon attivato" : "Sito online per tutti");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="mt-6 max-w-md border border-[#C9A227]/20 rounded-sm p-6 bg-[#0b0b0d]">
      <div className="flex items-center gap-2 text-[#C9A227]">
        <Globe size={16} />
        <span className="text-xs font-mono-ps uppercase tracking-widest">Modalità Coming Soon</span>
      </div>
      <p className="text-sm text-[#9CA3AF] mt-2">
        Quando è attiva, i visitatori vedono solo la pagina "Coming Soon" (più Privacy e Cookie Policy). Tu, da admin loggata, vedi il sito completo.
      </p>
      <div className="mt-5 flex items-center justify-between">
        <span data-testid="coming-soon-status" className={`text-sm font-mono-ps uppercase ${on ? "text-[#C9A227]" : "text-emerald-400"}`}>
          {on === null ? "…" : on ? "Attiva — sito nascosto" : "Disattiva — sito pubblico"}
        </span>
        <button onClick={toggle} disabled={on === null} data-testid="coming-soon-toggle" className="btn-gold disabled:opacity-60">
          {on ? "Lancia il sito" : "Attiva Coming Soon"}
        </button>
      </div>
    </div>
  );
};
